import React from 'react';
import { css } from '@emotion/react';
import { LinearProgress } from '@material-ui/core';
import { ReportProblemOutlined } from '@material-ui/icons';

import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';

import {
    CompactField,
    ConflictGroup,
    DecisionButton,
    DecisionNote,
    Divider,
    ExistingDot,
    ExistingIndex,
    ExistingMeta,
    ExistingName,
    ExistingRow,
    FieldRow,
    SectionNote,
    SectionTitle,
} from './blocks';
import { CONFLICT_ACTIONS } from './constants';
import { confirmLabel } from './utils';

const cityState = (match) => [match.business_city, match.business_state].filter(Boolean).join(', ');

const decisionNote = ({ action, connectTarget, canConnect }) => {
    if (action === CONFLICT_ACTIONS.SKIP) return 'Skipped — nothing will be added for this row.';
    if (action === CONFLICT_ACTIONS.ADD_ANYWAY) return 'Will be added as a separate account on the same DOT.';
    if (action === CONFLICT_ACTIONS.CONNECT) {
        return `Will connect ${connectTarget?.business_name || 'the existing account'} — no new account is created.`;
    }
    return canConnect
        ? 'Not decided yet — connect one of the accounts above, Skip, or Add Anyway.'
        : 'Not decided yet — pick Skip or Add Anyway.';
};

/**
 * One DOT collision: the accounts already on that DOT, then the incoming row with the
 * decision buttons.
 *
 * The existing records are numbered so the incoming row reads as the next entry in the
 * same list — that is what makes "you may have the same DOT for multiple locations"
 * legible at a glance. Connect sits on each existing account rather than on the
 * incoming row, so with several accounts on one DOT it's always clear which one gets
 * linked. An account already connected to this org has nothing to connect.
 *
 * A `duplicate` row matched nothing on Onward but repeats a DOT from earlier in the
 * same file, so instead of existing accounts it names that earlier row.
 */
const ConflictRow = ({ conflict, sourceRow, duplicateOf, resolution, busy, callbacks }) => {
    const { index, matches = [] } = conflict;
    const action = resolution?.action;
    // Name edits live in the resolution; the uploaded row is the fallback. DOT is
    // read-only — it's what the row was matched on, and an edit would skip that check.
    const name = resolution?.business_name ?? sourceRow?.business_name ?? '';
    const dot = sourceRow?.dot_number ?? '';
    const connectTarget =
        action === CONFLICT_ACTIONS.CONNECT ? matches.find((m) => m.client_id === resolution.client_id) : null;
    const canConnect = matches.some((m) => !m.connected);

    return (
        <ConflictGroup>
            {conflict.action === 'duplicate' ? (
                <ExistingRow>
                    <ExistingName title={duplicateOf?.business_name}>
                        Same DOT as row {conflict.duplicate_of + 1} in this file:{' '}
                        {duplicateOf?.business_name || '(no name)'}
                    </ExistingName>
                    <ExistingDot>DOT: {duplicateOf?.dot_number || '--'}</ExistingDot>
                </ExistingRow>
            ) : null}

            {matches.map((match, i) => {
                const selected = connectTarget?.client_id === match.client_id;
                return (
                    <ExistingRow key={match.client_id} highlighted={selected}>
                        <ExistingIndex>{i + 1}.</ExistingIndex>
                        <ExistingName title={match.business_name || match.client_id}>
                            {match.business_name || '(no name)'}
                        </ExistingName>
                        {cityState(match) ? <ExistingMeta>{cityState(match)}</ExistingMeta> : null}
                        {match.pending ? <ExistingMeta>invite pending</ExistingMeta> : null}
                        {match.connected ? <ExistingMeta>already connected</ExistingMeta> : null}
                        <ExistingDot>DOT: {match.dot_number || '--'}</ExistingDot>
                        {match.connected ? null : (
                            <DecisionButton
                                onClick={() => callbacks.connectConflict(index, match.client_id)}
                                disabled={busy}
                                selected={selected}
                            >
                                Connect
                            </DecisionButton>
                        )}
                    </ExistingRow>
                );
            })}

            <FieldRow
                css={css`
                    align-items: center;
                `}
            >
                <ExistingIndex
                    css={css`
                        font-size: 14px;
                        font-weight: 700;
                        color: ${colors.golds.primary};
                    `}
                >
                    {matches.length + 1}.
                </ExistingIndex>
                <CompactField
                    variant="outlined"
                    value={name}
                    onChange={(e) => callbacks.resolveConflict(index, { business_name: e.target.value })}
                    disabled={busy || action === CONFLICT_ACTIONS.SKIP || action === CONFLICT_ACTIONS.CONNECT}
                    css={css`
                        flex: 1 1 auto;
                    `}
                />
                <CompactField
                    variant="outlined"
                    value={dot}
                    disabled
                    css={css`
                        flex: 0 0 140px;
                    `}
                />
                <DecisionButton
                    onClick={() => callbacks.skipConflict(index)}
                    disabled={busy}
                    selected={action === CONFLICT_ACTIONS.SKIP}
                >
                    Skip
                </DecisionButton>
                <DecisionButton
                    onClick={() => callbacks.addConflictAnyway(index)}
                    disabled={busy}
                    selected={action === CONFLICT_ACTIONS.ADD_ANYWAY}
                >
                    Add Anyway
                </DecisionButton>
            </FieldRow>

            <DecisionNote pending={!action}>{decisionNote({ action, connectTarget, canConnect })}</DecisionNote>
        </ConflictGroup>
    );
};

/**
 * Raised between the match and commit steps when an uploaded DOT is already on Onward —
 * whether it resolves to one existing account or several.
 *
 * A flagged row has three outcomes and no default: Connect links one of the existing
 * accounts to this org, Skip leaves it out, Add Anyway creates a separate account on
 * the same DOT (the multiple-locations case). Nothing commits until every row is
 * decided, and re-picking an action clears it again.
 */
const UploadExceptionsModal = ({ state, callbacks }) => {
    const { conflictsOpen, conflicts, sourceRows, resolutions, busy, planned, allResolved } = state;
    const hasDuplicates = conflicts.some((c) => c.action === 'duplicate');

    return (
        <NavResponsiveModal open={!!conflictsOpen} onClose={callbacks.dismissConflicts} maxWidth="md" fullWidth>
            <ModalHeader
                title={
                    <span
                        css={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 10px;
                            color: ${colors.reds.primary};
                        `}
                    >
                        <ReportProblemOutlined />
                        Upload Exceptions
                    </span>
                }
                onClose={callbacks.dismissConflicts}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            {busy ? (
                <LinearProgress
                    color="primary"
                    css={css`
                        width: 100%;
                    `}
                />
            ) : null}
            <ModalContent>
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        padding-bottom: 12px;
                    `}
                >
                    <SectionTitle>
                        {hasDuplicates
                            ? 'These DOTs are already on Onward or repeated in this file.'
                            : 'These DOTs are already on Onward.'}
                    </SectionTitle>
                    <SectionNote>Pick Connect, Skip or Add Anyway for each row.</SectionNote>
                </div>

                {conflicts.map((conflict, i) => (
                    <React.Fragment key={conflict.index}>
                        {i > 0 ? (
                            <Divider
                                css={css`
                                    margin: 16px 0;
                                `}
                            />
                        ) : null}
                        <ConflictRow
                            conflict={conflict}
                            sourceRow={sourceRows[conflict.index]}
                            duplicateOf={conflict.action === 'duplicate' ? sourceRows[conflict.duplicate_of] : null}
                            resolution={resolutions[conflict.index]}
                            busy={busy}
                            callbacks={callbacks}
                        />
                    </React.Fragment>
                ))}
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={callbacks.dismissConflicts} disabled={busy}>
                    Cancel upload
                </SecondaryButton>
                <PrimaryButton onClick={callbacks.commitConflicts} disabled={busy || !allResolved} loading={busy}>
                    {/* Counts what will actually happen, so an all-skip batch doesn't
                        read as "Add 1 carrier". */}
                    {confirmLabel(planned)}
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default UploadExceptionsModal;
